import { useState } from 'react'
import { ScoopButton, ScoopMenu, ScoopMenuItem, ScoopRow, ScoopText } from '../../../ui'
import {
    DatabaseIcon,
    TrashIcon,
    DotsThreeVerticalIcon,
    TableIcon,
    LinkIcon,
} from '@phosphor-icons/react'
import { useModal, MODALS } from '../../../modals'
import styles from './LiveQueryRow.module.scss'

/**
 * LiveQueryRow - Displays a Live Query connection in a compact horizontal row
 */
export const LiveQueryRow = ({ table, onDelete, isDeleting }) => {
    const [menuAnchor, setMenuAnchor] = useState(null)
    const deleteModal = useModal(MODALS.COMMON.DELETE)

    const handleMenuOpen = (event) => {
        event.stopPropagation()
        setMenuAnchor(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchor(null)
    }

    const handleDeleteClick = async () => {
        handleMenuClose()
        const confirmed = await deleteModal.show({
            title: 'Live Query',
            description: `Are you sure you want to delete "${table.label || table.inboxName}"? This will remove the connection and cannot be undone.`,
        })
        if (confirmed) {
            onDelete({ inboxName: table.inboxName, tableName: table.tableName })
        }
    }

    // Extract connection info from extractDefinition if available
    const getConnectionInfo = () => {
        const extractDef = table.extractDefinition
        if (!extractDef?.object?.connectorDefinitionFields) {
            return { driver: 'Unknown', host: 'Unknown' }
        }

        const fields = extractDef.object.connectorDefinitionFields
        const jdbcUrl = fields.find((f) => f.name === 'jdbcUrl')?.value || ''

        // Try to parse driver type from URL
        let driver = extractDef.connectorType || 'Database'
        if (jdbcUrl.includes('snowflake')) driver = 'Snowflake'
        else if (jdbcUrl.includes('postgresql')) driver = 'PostgreSQL'
        else if (jdbcUrl.includes('mysql')) driver = 'MySQL'
        else if (jdbcUrl.includes('mariadb')) driver = 'MariaDB'
        else if (jdbcUrl.includes('sqlserver')) driver = 'SQL Server'
        else if (jdbcUrl.includes('oracle')) driver = 'Oracle'

        // Try to extract host from JDBC URL
        let host = 'Custom connection'
        const hostMatch = jdbcUrl.match(/:\/\/([^:/]+)/)
        if (hostMatch) {
            host = hostMatch[1]
        }

        return { driver, host }
    }

    const { driver, host } = getConnectionInfo()
    const dimensionCount = table.joinRelationships?.length || 0

    return (
        <>
            <div className={styles.row}>
                <div className={styles.iconWrapper}>
                    <DatabaseIcon size={20} weight="regular" />
                </div>

                <div className={styles.nameSection}>
                    <ScoopText className={styles.tableName}>
                        {table.label || table.inboxName}
                    </ScoopText>
                    {table.description && (
                        <ScoopText className={styles.description}>{table.description}</ScoopText>
                    )}
                </div>

                <ScoopRow className={styles.infoSection}>
                    <div className={styles.infoPill}>
                        <ScoopText className={styles.infoText}>{driver}</ScoopText>
                    </div>
                </ScoopRow>

                <ScoopRow className={styles.infoSection}>
                    <ScoopText className={styles.hostText}>{host}</ScoopText>
                </ScoopRow>

                {table.externalTableName && (
                    <ScoopRow className={styles.tableSection}>
                        <TableIcon size={14} />
                        <ScoopText className={styles.tableText}>
                            {table.externalTableName}
                        </ScoopText>
                    </ScoopRow>
                )}

                {dimensionCount > 0 && (
                    <ScoopRow className={styles.joinSection}>
                        <LinkIcon size={14} />
                        <ScoopText className={styles.joinText}>
                            {dimensionCount} dimension{dimensionCount > 1 ? 's' : ''}
                        </ScoopText>
                    </ScoopRow>
                )}

                <ScoopRow className={styles.actionsSection}>
                    <div className={styles.typeBadge}>
                        <ScoopText className={styles.typeText}>Live Query</ScoopText>
                    </div>
                    <ScoopButton
                        variant="icon"
                        size="xs"
                        className={styles.menuButton}
                        onClick={handleMenuOpen}
                    >
                        <DotsThreeVerticalIcon size={18} />
                    </ScoopButton>
                </ScoopRow>
            </div>

            {/* Context Menu */}
            <ScoopMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <ScoopMenuItem
                    onClick={handleDeleteClick}
                    leftSection={<TrashIcon size={16} />}
                    primary="Delete"
                    className={styles.deleteMenuItem}
                />
            </ScoopMenu>
        </>
    )
}
